import Page from 'src/components/Page';
import {
    Card, CardContent, Container,
    Link, Stack, TextField, Typography,
    useMediaQuery, Switch,
    Autocomplete
}
    from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useFormik, Form, FormikProvider, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { capitalizeWords } from 'src/utils/commonUtils';
import { debounce } from 'lodash';

export default function AddUserForm() {
    const id = useParams();
    const hrToken = localStorage.getItem("authToken");
    const navigate = useNavigate();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'));
    const [userData, setUserData] = useState([]);
    const [searchCompanyQuery, setSearchCompanyQuery] = useState(null);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    
    const debouncedCompanySearch = debounce((query) => {
        setSearchCompanyQuery(query);
    }, 1000);

    const getUserData = () => {
        apiInstance({
            method: "get",
            url: "users-viewset/" + id.id,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setUserData(response.data.data);
            })
            .catch(function (error) {
                console.error('Error fetching user data:', error);
                showErrorToast(error);
            });
    }

    const [practiceData, setPracticeData] = useState(null);
    const getPracticeData = () => {
        apiInstance({
            method: `get`,
            url: `practice/?short_details=${true}`,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setPracticeData(response.data.data);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    const [offeringData, setOfferingData] = useState(null);
    const getOfferingData = () => {
        apiInstance({
            method: `get`,
            url: `offering/?active_only=true`,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setOfferingData(response.data.data);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    const [segmentMasterData, setSegmentMasterData] = useState(null);
    const getSegmentMasterData = () => {
        apiInstance({
            method: `get`,
            url: `segment-master/?active_only=true`,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setSegmentMasterData(response.data.data);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    const [segmentData, setSegmentData] = useState(null);
    const getSegmentData = () => {
        apiInstance({
            method: `get`,
            url: `segment/?active_only=true`,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setSegmentData(response.data.data);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    const [countryData, setCountryData] = useState(null);
    const getCountryData = () => {
        apiInstance({
            method: `get`,
            url: `location/?short_details=${true}`,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                setCountryData(response.data.data);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    const [companyData, setCompanyData] = useState(null);
    const getCompanyData = (searchCompanyQuery = '') => {
        var apiUrl = `company/?page=1&page_size=5&short_details=${true}`
        if (searchCompanyQuery) apiUrl = `company/?page=1&page_size=5&short_details=${true}&search=${searchCompanyQuery}&`
        
        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        apiInstance({
            method: `get`,
            url: apiUrl,
            headers: {
                Authorization: "token " + hrToken,
            }
        })
            .then(function (response) {
                const searchResults = response?.data?.results?.data || [];
                
                // Combine selected companies with search results, avoiding duplicates
                const combinedOptions = [
                    ...selectedCompanies,
                    ...searchResults.filter(result => 
                        !selectedCompanies.some(selected => selected.id === result.id)
                    )
                ];
                
                setCompanyData(combinedOptions);
            })
            .catch(function (error) {
                showErrorToast(error);
            });
    }

    // Load initial data
    useEffect(() => {
        getPracticeData();
        getOfferingData();
        getSegmentMasterData();
        getSegmentData();
        getCountryData();
        if (!searchCompanyQuery) getCompanyData();
    }, [])

    // Handle company search changes
    useEffect(() => {
        if (searchCompanyQuery !== null) getCompanyData(searchCompanyQuery);
    }, [searchCompanyQuery, selectedCompanies])

    // Load user data when editing
    useEffect(() => {
        if (id?.id) getUserData();
    }, [id, id?.id])

    const callAddUserAPI = (formValues) => {
        apiInstance({
            method: "post",
            url: "users-viewset/",
            data: formValues,
        })
            .then(function (response) {
                showToast(response.data.message)
                navigate("/users", { replace: true });
            })
            .catch(function (error) {
                showErrorToast(error);
                setSubmitting(false)
            });
    }

    const callEditUserAPI = (formValues) => {
        apiInstance({
            method: "put",
            url: "users-viewset/" + id.id + "/",
            data: formValues,
        })
            .then(function (response) {
                showToast(response.data.message || 'User updated successfully');
                // Refetch user data to ensure UI is in sync
                getUserData();
                setTimeout(() => {
                    navigate("/users", { replace: true });
                }, 500);
            })
            .catch(function (error) {
                console.error('Error updating user:', error);
                showErrorToast(error);
                setSubmitting(false);
            });
    }

    const Schema = Yup.object().shape({
        first_name: Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .matches(/^[a-zA-Z]+$/, "Only alphabets without spaces are allowed for first name ")
            .required("First name required"),
        middle_name: Yup.string(),
        last_name: Yup.string()
            .min(1, "Too Short!")
            .max(50, "Too Long!")
            .matches(/^[a-zA-Z]+$/, "Only alphabets without spaces are allowed for last name ")
            .required("Last name required"),
        username: Yup.string()
            .required("ERP username is required"),
        email: Yup.string()
            .email("Email must be a valid email address")
            .test("is-lowercase", "Email must be in lowercase", (value) => value === value?.toLowerCase())
            .required("Email is required"),
        emp_code: Yup.string()
            .required("Employee code is required")
            .max(100, "Employee code too long!")
            .matches(/^[a-zA-Z0-9-_]+$/, "Only alphanumeric characters, hyphens and underscores are allowed"),
        country: Yup.array(),
        practice: Yup.array(),
        company: Yup.array(),
        offering: Yup.array(),
        segment_master: Yup.array(),
        segment: Yup.array(),
        is_active: Yup.string()
            .required("Active status is required."),
    });

    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            username: '',
            emp_code: '',
            country: [],
            practice: [],
            company: [],
            offering: [],
            segment_master: [],
            segment: [],
            is_active: '',
        },
        validationSchema: Schema,
        onSubmit: (values) => {
            let formData = {
                "first_name": values.first_name,
                "last_name": values.last_name,
                "email": values.email,
                "username": values.username,
                "emp_code": values.emp_code,
                "country": values.country,
                "practice": values.practice,
                "company": values.company,
                "offering": values.offering,
                "segment_master": values.segment_master,
                "segment": values.segment,
                "is_active": values.is_active,
            }
            if (id?.id) {
                callEditUserAPI(formData)
            } else {
                callAddUserAPI(formData)
            }
        }
    });

    // Handle user data loading and form population
    useEffect(() => {
        if (id && userData && Object.keys(userData).length > 0) {
            
            
            const userCompanies = userData?.company || [];
            const userCountries = userData?.country || [];
            const userPractices = userData?.practice || [];
            const userOfferings = userData?.offering || [];
            const userSegmentMasters = userData?.segment_master || [];
            const userSegments = userData?.segment || [];


            // First set the selected companies from user data
            setSelectedCompanies(userCompanies);
            
            // Then ensure these companies are included in the dropdown options
            if (userCompanies.length > 0) {
                setCompanyData(prevData => {
                    // If no previous data exists, start with user companies
                    if (!prevData || prevData.length === 0) {
                        return userCompanies;
                    }
                    
                    // Merge user companies with existing data, avoiding duplicates
                    const existingIds = prevData.map(c => c.id);
                    const newCompanies = userCompanies.filter(c => !existingIds.includes(c.id));
                    return [...newCompanies, ...prevData];
                });
            }

            // Merge granted (possibly since-deactivated) records into the active-only
            // option arrays so pre-populated values are never dropped (dedup by id).
            if (userOfferings.length > 0) {
                setOfferingData(prevData => {
                    if (!prevData || prevData.length === 0) {
                        return userOfferings;
                    }
                    const existingIds = prevData.map(o => o.id);
                    const newOfferings = userOfferings.filter(o => !existingIds.includes(o.id));
                    return [...newOfferings, ...prevData];
                });
            }
            if (userSegmentMasters.length > 0) {
                setSegmentMasterData(prevData => {
                    if (!prevData || prevData.length === 0) {
                        return userSegmentMasters;
                    }
                    const existingIds = prevData.map(s => s.id);
                    const newSegmentMasters = userSegmentMasters.filter(s => !existingIds.includes(s.id));
                    return [...newSegmentMasters, ...prevData];
                });
            }
            if (userSegments.length > 0) {
                setSegmentData(prevData => {
                    if (!prevData || prevData.length === 0) {
                        return userSegments;
                    }
                    const existingIds = prevData.map(s => s.id);
                    const newSegments = userSegments.filter(s => !existingIds.includes(s.id));
                    return [...newSegments, ...prevData];
                });
            }

            // Extract IDs from nested objects or use directly if already IDs
            const countryIds = Array.isArray(userCountries) 
                ? userCountries.map(c => typeof c === 'object' ? c.id : c)
                : [];
            const practiceIds = Array.isArray(userPractices)
                ? userPractices.map(p => typeof p === 'object' ? p.id : p)
                : [];
            const companyIds = Array.isArray(userCompanies)
                ? userCompanies.map(c => typeof c === 'object' ? c.id : c)
                : [];
            const offeringIds = Array.isArray(userOfferings)
                ? userOfferings.map(o => typeof o === 'object' ? o.id : o)
                : [];
            const segmentMasterIds = Array.isArray(userSegmentMasters)
                ? userSegmentMasters.map(s => typeof s === 'object' ? s.id : s)
                : [];
            const segmentIds = Array.isArray(userSegments)
                ? userSegments.map(s => typeof s === 'object' ? s.id : s)
                : [];

            // Set form values
            formik.setValues({
                first_name: userData?.first_name || '',
                last_name: userData?.last_name || '',
                username: userData?.username || '',
                email: userData?.email || '',
                emp_code: userData?.emp_code || '',
                country: countryIds,
                practice: practiceIds,
                company: companyIds,
                offering: offeringIds,
                segment_master: segmentMasterIds,
                segment: segmentIds,
                is_active: userData?.is_active !== undefined ? userData.is_active : false,
            });
        }
    }, [id, userData]);

    const handleNameChange = (field) => (event) => {
        const capitalizedValue = capitalizeWords(event.target.value);
        setFieldValue(field, capitalizedValue);
    };

    const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setSubmitting, setFieldValue, handleChange } = formik;

    return (
        <Page title={id?.id ? "Update User" : "Add User"}>
            <Container minWidth="xl">
                <Stack direction="row" sx={{ mb: 2, justifyContent: "space-between", alignItems: "center" }}>
                    <Link to="/users" color="green" underline="hover" component={RouterLink} fontSize="20px">
                        <ArrowCircleLeftIcon fontSize="large" />
                    </Link>
                    <Typography variant="h4" sx={{ my: matches ? 0 : 5 }} align="center" color="black">
                        {id?.id ? "Update User" : "Add User"}
                    </Typography>
                    <div></div>
                </Stack>
                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <Card sx={{ mt: 2, marginInline: 'auto' }} variant="outlined" style={{ width: matches ? '70%' : '50%' }}>
                            <CardContent>
                                <Stack spacing={3}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            required
                                            {...getFieldProps('first_name')}
                                            error={Boolean(touched.first_name && errors.first_name)}
                                            helperText={touched.first_name && errors.first_name}
                                            onChange={handleNameChange("first_name")}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            required
                                            {...getFieldProps('last_name')}
                                            error={Boolean(touched.last_name && errors.last_name)}
                                            helperText={touched.last_name && errors.last_name}
                                            onChange={handleNameChange("last_name")}
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="ERP Username"
                                            required
                                            {...getFieldProps('username')}
                                            error={Boolean(touched.username && errors.username)}
                                            helperText={touched.username && errors.username}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            required
                                            {...getFieldProps('email')}
                                            error={Boolean(touched.email && errors.email)}
                                            helperText={touched.email && errors.email}
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Employee Code"
                                            {...getFieldProps('emp_code')}
                                            error={Boolean(touched.emp_code && errors.emp_code)}
                                            helperText={touched.emp_code && errors.emp_code}
                                            placeholder="Auto-generated if left empty"
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {countryData?.length > 0 ?
                                            <Autocomplete
                                                fullWidth
                                                id="country"
                                                multiple
                                                options={countryData}
                                                getOptionLabel={(option) => option.label}
                                                value={(() => {
                                                    const selectedCountries = countryData?.filter((l) => values?.country?.includes(l.id));
                                                    return selectedCountries;
                                                })()}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("country", selectedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Country"
                                                        error={Boolean(touched.country && errors.country)}
                                                        helperText={touched.country && errors.country}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {practiceData?.length > 0 ?
                                            <Autocomplete
                                                fullWidth
                                                id="practice"
                                                multiple
                                                options={practiceData}
                                                getOptionLabel={(option) => option.label}
                                                value={practiceData?.filter((p) => values?.practice?.includes(p.id))}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("practice", selectedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Practice"
                                                        error={Boolean(touched.practice && errors.practice)}
                                                        helperText={touched.practice && errors.practice}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {offeringData?.length > 0 ?
                                            <Autocomplete
                                                fullWidth
                                                id="offering"
                                                multiple
                                                options={offeringData || []}
                                                getOptionLabel={(o) => o.practice_details ? `${o.label} (${o.practice_details.label})` : o.label}
                                                value={(offeringData || []).filter((o) => values?.offering?.includes(o.id))}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("offering", selectedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Offering"
                                                        error={Boolean(touched.offering && errors.offering)}
                                                        helperText={touched.offering && errors.offering}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {segmentMasterData?.length > 0 ?
                                            <Autocomplete
                                                fullWidth
                                                id="segment_master"
                                                multiple
                                                options={segmentMasterData || []}
                                                getOptionLabel={(o) => o.offering_label ? `${o.label} (${o.offering_label})` : o.label}
                                                value={(segmentMasterData || []).filter((s) => values?.segment_master?.includes(s.id))}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("segment_master", selectedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Segment Master"
                                                        error={Boolean(touched.segment_master && errors.segment_master)}
                                                        helperText={touched.segment_master && errors.segment_master}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {segmentData?.length > 0 ?
                                            <Autocomplete
                                                fullWidth
                                                id="segment"
                                                multiple
                                                options={segmentData || []}
                                                getOptionLabel={(o) => o.segment_master_label ? `${o.label} (${o.segment_master_label})` : o.label}
                                                value={(segmentData || []).filter((s) => values?.segment?.includes(s.id))}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("segment", selectedIds);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Segment"
                                                        error={Boolean(touched.segment && errors.segment)}
                                                        helperText={touched.segment && errors.segment}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        {companyData?.length > 0 || searchCompanyQuery ?
                                            <Autocomplete
                                                fullWidth
                                                id="company"
                                                multiple
                                                options={companyData || []}
                                                onInputChange={(event, value) => { 
                                                    if (value?.length >= 1 && value?.length <= 20) debouncedCompanySearch(value) 
                                                }}
                                                value={companyData?.filter((c) => values?.company?.includes(c.id)) || []}
                                                onChange={(event, value) => {
                                                    const selectedIds = value.map((item) => item.id);
                                                    setFieldValue("company", selectedIds);
                                                    setSelectedCompanies(value);
                                                }}
                                                getOptionLabel={(option) =>
                                                    option?.company_name + (option?.website ? ` | ${option.website}` : '')
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        fullWidth
                                                        {...params}
                                                        label="Company"
                                                        error={Boolean(touched.company && errors.company)}
                                                        helperText={touched.company && errors.company}
                                                    />
                                                )}
                                            />
                                            : null}
                                    </Stack>
                                    <Stack>
                                        <div role="group" style={{ width: '40%' }}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={3}
                                                style={{ padding: "20px 0 0 12px" }}
                                            >
                                                <div id="switch-label" style={{ color: '#637381', marginTop: '5px' }}> Is Active? </div>
                                                <Switch
                                                    name="is_active"
                                                    checked={values.is_active}
                                                    onChange={(event, checked) => {
                                                        setFieldValue("is_active", checked);
                                                    }}
                                                />
                                            </Stack>
                                            <ErrorMessage name="is_active">
                                                {(msg) => <span style={{ color: "#FF4842", fontSize: "12px", marginLeft: 15 }}>{msg}</span>}
                                            </ErrorMessage>
                                        </div>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }} />
                        <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                            style={{ display: "block", margin: "auto", width: 300 }}
                        >
                            {id?.id ? "Update User" : "Add User"}
                        </LoadingButton>
                    </Form>
                </FormikProvider>
            </Container>
        </Page>
    )
}